import {
  ButtonGroup,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {Grid, GridItem, HStack, Show, VStack} from 'platform/foundation';

import {useEffect, useState} from 'react';

import {isNil, not} from 'ramda';
import {isFalse, isNotNil} from 'ramda-adjunct';

import {PaymentTypeResponseBody} from '@dms/api/core';
import {CustomerResponseBodyV2} from '@dms/api/customer';
import {BaseAuthorizationProfile} from '@dms/api/security';
import {CurrencyResponseBody, PaymentType} from '@dms/api/shared';
import {
  GetWarehousesResponse,
  PatchReceiveNoteCompletedRequest,
  PatchReceiveNoteManualRequest,
  PostReceiveNoteManualRequest,
  ReceiveNoteDeliveryNoteSlice,
  ReceiveNoteGeneralInfoSlice,
  ReceiveNoteStateSlice,
  useLazyGetWarehouseQuery,
  useLazyGetWarehousesQuery,
  usePatchReceiveNoteCompletedMutation,
  usePatchReceiveNoteManualMutation,
  usePostReceiveNoteManualMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';
import {
  getOptionsFromAuthorizationProfiles,
  getOptionsFromCurrencies,
  getOptionsFromCustomersAsSupplier,
  getOptionsFromWarehouses,
  handleApiError,
  usePermissions,
} from '@dms/shared';
import {getOptionsFromPaymentTypes} from '@dms/warehouse/shared';

import {
  composePath,
  getApiDateString,
  Nullish,
  parseDate,
  RequiredTestIdProps,
  suffixTestId,
  useNavigate,
} from 'shared/web';

import {
  ReceiveNoteBasicInformationForm,
  ReceiveNoteBasicInformationFormSchema,
} from '../schemas/ReceiveNoteBasicInformationFormSchema';

const DEFAULT_PAYMENT_TYPE = 'bank_transfer';

export interface BasicInformationFormProps extends RequiredTestIdProps {
  isCompleted: boolean;
  isDraft: boolean;
  suppliers: CustomerResponseBodyV2[] | Nullish;
  warehouses: GetWarehousesResponse | Nullish;
  authorizationProfiles: BaseAuthorizationProfile[] | Nullish;
  paymentTypes: PaymentTypeResponseBody[] | Nullish;
  currencies?: CurrencyResponseBody[] | Nullish;
  receiveNoteState?: ReceiveNoteStateSlice | Nullish;
  receiveNoteGeneralInfo?: ReceiveNoteGeneralInfoSlice | Nullish;
  receiveNoteDeliveryNote?: ReceiveNoteDeliveryNoteSlice | Nullish;
  currencyCodeByWarehouse: string | Nullish;
  onClose: () => void;
}

export function BasicInformationForm(props: BasicInformationFormProps) {
  const navigate = useNavigate();

  const [canEditReceiveNote] = usePermissions({
    permissionKeys: ['receiveNoteEdit'],
  });

  const [availableWarehousesToSupplier, setAvailableWarehousesToSupplier] = useState<
    GetWarehousesResponse | Nullish
  >(props.warehouses);

  const [postReceiveNoteManual] = usePostReceiveNoteManualMutation();
  const [patchReceiveNoteManual] = usePatchReceiveNoteManualMutation();
  const [patchReceiveNoteCompleted] = usePatchReceiveNoteCompletedMutation();
  const [getWarehouse, {isFetching: isWarehouseFetching}] = useLazyGetWarehouseQuery();
  const [getWarehouses, {isFetching: areWarehousesFetching}] = useLazyGetWarehousesQuery();

  const isFromReceiveNote = props.receiveNoteDeliveryNote?.deliveryNote.isFromReceiveNote;
  const isReceiveNoteCompleted = props.receiveNoteState?.state === 'COMPLETED';
  const supplierId = props.receiveNoteGeneralInfo?.supplierId;
  const authorizationProfileId = props.receiveNoteGeneralInfo?.authorizationProfileId;

  useEffect(
    function syncAvailableWarehousesToSupplier() {
      if (supplierId && authorizationProfileId) {
        getWarehouses({
          supplierId,
          authorizationProfileId,
        })
          .unwrap()
          .then(setAvailableWarehousesToSupplier)
          .catch(handleApiError);
      } else {
        return;
      }
    },
    [getWarehouses, supplierId, authorizationProfileId]
  );

  const defaultValues: Partial<ReceiveNoteBasicInformationForm> = {
    supplierId: props.receiveNoteGeneralInfo?.supplierId,
    warehouseId: props.receiveNoteGeneralInfo?.warehouseId,
    authorizationProfileId: props.receiveNoteGeneralInfo?.authorizationProfileId,
    number: props.receiveNoteState?.receiveNoteNumber,
    date:
      isReceiveNoteCompleted && props.receiveNoteState?.completedAt
        ? parseDate(props.receiveNoteState.completedAt)
        : null,
    invoiceNumber: props.receiveNoteGeneralInfo?.invoiceNumber,
    invoiceIssueDate: parseDate(props.receiveNoteGeneralInfo?.invoiceIssueDate),
    paymentType: props.receiveNoteGeneralInfo?.paymentType ?? DEFAULT_PAYMENT_TYPE,
    currency: props.currencyCodeByWarehouse ?? undefined,
    exchangeRate: props.receiveNoteGeneralInfo?.exchangeRate,
    ncConversion: props.receiveNoteGeneralInfo?.ncConversion,
    deliveryNoteNumber: props.receiveNoteDeliveryNote?.deliveryNote?.deliveryNoteNumber,
    deliveryNoteIssueDate: parseDate(
      props.receiveNoteDeliveryNote?.deliveryNote?.deliveryNoteIssuedAt
    ),
    note: props.receiveNoteGeneralInfo?.note,
  };

  const handleSubmit: FormSubmitHandler<ReceiveNoteBasicInformationForm> = async (formValues) => {
    if (props.receiveNoteState) {
      if (props.isCompleted) {
        const receiveNoteCompletedBody: PatchReceiveNoteCompletedRequest['body'] = {
          deliveryNoteNumber: formValues.deliveryNoteNumber,
          invoiceNumber: formValues.invoiceNumber,
          invoiceIssueDate: formValues.invoiceIssueDate
            ? getApiDateString(formValues.invoiceIssueDate)
            : null,
          note: formValues.note,
          issuedAt: formValues.deliveryNoteIssueDate?.toISOString(),
        };

        return await patchReceiveNoteCompleted({
          receiveNoteId: props.receiveNoteState.receiveNoteId,
          body: receiveNoteCompletedBody,
        })
          .unwrap()
          .then(props.onClose)
          .catch(handleApiError);
      }

      const receiveNoteBody: PatchReceiveNoteManualRequest['body'] = {
        supplierId: formValues.supplierId,
        warehouseId: formValues.warehouseId,
        authorizationProfileId: formValues.authorizationProfileId,
        deliveryNoteNumber: formValues.deliveryNoteNumber,
        invoiceNumber: formValues.invoiceNumber,
        invoiceIssueDate: formValues.invoiceIssueDate
          ? getApiDateString(formValues.invoiceIssueDate)
          : null,
        paymentType: formValues.paymentType.toLowerCase() as PaymentType,
        exchangeRate: formValues.exchangeRate,
        ncConversion: formValues.ncConversion,
        issuedAt: formValues.deliveryNoteIssueDate?.toISOString(),
        note: formValues.note,
      };

      return await patchReceiveNoteManual({
        receiveNoteId: props.receiveNoteState.receiveNoteId,
        body: receiveNoteBody,
      })
        .unwrap()
        .then(props.onClose)
        .catch(handleApiError);
    }

    const receiveNoteBody: PostReceiveNoteManualRequest = {
      supplierId: formValues.supplierId,
      warehouseId: formValues.warehouseId,
      authorizationProfileId: formValues.authorizationProfileId,
      deliveryNoteNumber: formValues.deliveryNoteNumber,
      invoiceNumber: formValues.invoiceNumber,
      invoiceIssueDate: formValues.invoiceIssueDate
        ? getApiDateString(formValues.invoiceIssueDate)
        : null,
      paymentType: formValues.paymentType.toLowerCase() as PaymentType,
      exchangeRate: formValues.exchangeRate,
      ncConversion: formValues.ncConversion,
      note: formValues.note,
      delivery: {
        issuedAt: formValues.deliveryNoteIssueDate?.toISOString(),
      },
    };

    await postReceiveNoteManual(receiveNoteBody)
      .unwrap()
      .then((response) => {
        showNotification.success(i18n.t('page.warehouse.notification.receiveNoteCreated'));
        props.onClose();
        navigate(
          composePath(warehouseRoutes.receiveNoteDetailOverview, {
            params: {id: response.receiveNoteId},
          })
        );
      })
      .catch(handleApiError);
  };

  return (
    <Form<ReceiveNoteBasicInformationForm>
      isDisabled={not(props.isDraft) && not(canEditReceiveNote)}
      experimentalZodSchema={ReceiveNoteBasicInformationFormSchema}
      onSubmit={handleSubmit}
      defaultValues={defaultValues}
    >
      {(control, formApi) => {
        const supplierId = formApi.watch('supplierId');

        const applyWarehouseSelection = (warehouseId: string, selectedSupplierId: string) =>
          getWarehouse({warehouseId})
            .unwrap()
            .then((warehouse) => {
              if (
                formApi.getValues('supplierId') !== selectedSupplierId ||
                formApi.getValues('warehouseId') !== warehouseId
              ) {
                return;
              }

              formApi.setValue('authorizationProfileId', warehouse.authorizationProfileId, {
                shouldValidate: true,
              });

              formApi.setValue('currency', warehouse.currency, {shouldValidate: true});
            })
            .catch(handleApiError);

        const handleWarehouseChange = (newWarehouseId: string | number | null) => {
          const selectedSupplierId = formApi.getValues('supplierId');

          if (typeof newWarehouseId !== 'string' || typeof selectedSupplierId !== 'string') {
            return;
          }

          return applyWarehouseSelection(newWarehouseId, selectedSupplierId);
        };

        const handleSupplierChange = (newSupplierId: string | string[] | number | null) => {
          if (typeof newSupplierId !== 'string') {
            return;
          }

          return getWarehouses({supplierId: newSupplierId})
            .unwrap()
            .then((warehouses) => {
              if (formApi.getValues('supplierId') !== newSupplierId) {
                return;
              }

              setAvailableWarehousesToSupplier(warehouses);

              const [warehouse] = warehouses;

              if (warehouses.length === 1 && isNotNil(warehouse)) {
                formApi.setValue('warehouseId', warehouse.id, {shouldValidate: true});
                return applyWarehouseSelection(warehouse.id, newSupplierId);
              }

              const warehouseId = formApi.getValues('warehouseId');
              const isWarehouseAvailable = warehouses.some(
                (availableWarehouse) => availableWarehouse.id === warehouseId
              );

              if (isNotNil(warehouseId) && not(isWarehouseAvailable)) {
                formApi.reset({
                  ...formApi.getValues(),
                  warehouseId: undefined,
                  authorizationProfileId: undefined,
                  currency: undefined,
                });
              }
            })
            .catch(handleApiError);
        };

        return (
          <>
            <VStack spacing={4}>
              <Grid columns={4}>
                <FormField
                  isRequired
                  isNotClearable
                  isDisabled={isFalse(isFromReceiveNote) || props.isCompleted}
                  control={control}
                  type="choice"
                  name="supplierId"
                  label={i18n.t('entity.warehouse.labels.supplier')}
                  options={getOptionsFromCustomersAsSupplier(props.suppliers)}
                  onChange={handleSupplierChange}
                  data-testid={suffixTestId('inputs.supplierId', props)}
                />
                <FormField
                  isRequired
                  isNotClearable
                  isLoading={areWarehousesFetching}
                  isDisabled={isNil(supplierId) || props.isCompleted}
                  control={control}
                  type="choice"
                  name="warehouseId"
                  label={i18n.t('entity.warehouse.labels.warehouse')}
                  options={getOptionsFromWarehouses(availableWarehousesToSupplier)}
                  onChange={handleWarehouseChange}
                  data-testid={suffixTestId('inputs.warehouseId', props)}
                />
                <FormField
                  isRequired
                  isNotClearable
                  isLoading={isWarehouseFetching}
                  isDisabled
                  control={control}
                  type="choice"
                  name="authorizationProfileId"
                  label={i18n.t('entity.warehouse.labels.authorizationProfile')}
                  options={getOptionsFromAuthorizationProfiles(
                    props.authorizationProfiles,
                    formApi.watch('authorizationProfileId')
                  )}
                  data-testid={suffixTestId('inputs.authorizationProfileId', props)}
                />
              </Grid>

              <Grid columns={4}>
                <FormField
                  control={control}
                  type="text"
                  name="deliveryNoteNumber"
                  label={i18n.t('entity.warehouse.labels.deliveryNoteNumber')}
                  data-testid={suffixTestId('inputs.deliveryNoteNumber', props)}
                />
                <FormField
                  control={control}
                  type="date"
                  name="deliveryNoteIssueDate"
                  label={i18n.t('entity.warehouse.labels.deliveryNoteIssueDate')}
                  data-testid={suffixTestId('inputs.deliveryNoteIssueDate', props)}
                />
                <FormField
                  control={control}
                  type="text"
                  name="invoiceNumber"
                  label={i18n.t('entity.warehouse.labels.invoiceNo')}
                  data-testid={suffixTestId('inputs.invoiceNumber', props)}
                />
                <FormField
                  control={control}
                  type="date"
                  name="invoiceIssueDate"
                  label={i18n.t('entity.warehouse.labels.invoiceIssueDate')}
                  data-testid={suffixTestId('inputs.invoiceIssueDate', props)}
                />
              </Grid>

              <Grid columns={4}>
                <FormField
                  isRequired
                  isDisabled={props.isCompleted}
                  isNotClearable
                  control={control}
                  type="choice"
                  name="paymentType"
                  label={i18n.t('entity.warehouse.labels.paymentType')}
                  options={getOptionsFromPaymentTypes(props.paymentTypes)}
                  data-testid={suffixTestId('inputs.paymentType', props)}
                />
                <FormField
                  isRequired
                  isDisabled
                  control={control}
                  type="choice"
                  name="currency"
                  label={i18n.t('entity.warehouse.labels.currency')}
                  options={getOptionsFromCurrencies(props.currencies)}
                  data-testid={suffixTestId('inputs.currency', props)}
                />
                <FormField
                  control={control}
                  type="text"
                  name="exchangeRate"
                  label={i18n.t('entity.warehouse.labels.exchangeRate')}
                  isDisabled
                  helperText="Not implemented"
                  data-testid={suffixTestId('inputs.exchangeRate', props)}
                />
                <FormField
                  control={control}
                  type="choice"
                  name="ncConversion"
                  label={i18n.t('entity.warehouse.labels.ncConversion')}
                  options={[]}
                  isDisabled
                  helperText="Not implemented"
                  data-testid={suffixTestId('inputs.ncConversion', props)}
                />
              </Grid>

              <Grid columns={4}>
                <GridItem span={4}>
                  <FormField
                    control={control}
                    type="textarea"
                    name="note"
                    label={i18n.t('entity.warehouse.labels.note')}
                    data-testid={suffixTestId('inputs.note', props)}
                  />
                </GridItem>
              </Grid>

              <HStack justify="flex-end">
                <ButtonGroup>
                  <Show when={isNotNil(props.receiveNoteState)}>
                    <FormButton
                      control={control}
                      type="reset"
                      variant="secondary"
                      title={i18n.t('general.labels.discard')}
                      onClick={props.onClose}
                      data-testid={suffixTestId('actions.discard', props)}
                    />
                  </Show>
                  <FormButton
                    control={control}
                    type="submit"
                    variant="primary"
                    title={i18n.t('general.labels.saveChanges')}
                    data-testid={suffixTestId('actions.submit', props)}
                  />
                </ButtonGroup>
              </HStack>
            </VStack>
          </>
        );
      }}
    </Form>
  );
}
